import { mapGetters } from 'vuex';

const WS_PATH = '/data-api/whatsapp/ws';
const CONNECT_TIMEOUT_MS = 10000;
const SLOW_POLL_AFTER_ATTEMPTS = 10;
const SLOW_POLL_MS = 60000;
const MAX_BACKOFF_MS = 30000;

/**
 * Live WhatsApp events for the admin inbox.
 *
 * Lifted from `vue-common/components/chat-view/llama-chat.vue`'s socket
 * handling. The watchdog, the never-give-up backoff and the visibilitychange
 * reconnect all exist there because they were needed — a chat socket that dies
 * silently looks exactly like an inbox with no new messages.
 *
 * The consuming component implements `onWhatsAppEvent(event)`.
 */
export default {
  data() {
    return {
      ws: null,
      wsAuthenticated: false,
      wsRetryCount: 0,
      wsReconnectTimer: null,
      _wsConnectTimeout: null,
    };
  },

  computed: {
    ...mapGetters(['access_token']),
  },

  watch: {
    // The token can arrive after mount (page reload restores it asynchronously).
    access_token(token) {
      if (token) this.ensureWebSocketConnected();
    },
  },

  mounted() {
    this._onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        this.ensureWebSocketConnected();
      }
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);
    this.connectWebSocket();
  },

  beforeDestroy() {
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    this.disconnectWebSocket();
  },

  methods: {
    connectWebSocket() {
      if (!this.access_token) {
        // The access_token watcher will start us once it lands.
        return;
      }
      this.disconnectWebSocket();

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url = `${protocol}//${window.location.host}${WS_PATH}`;
      this.ws = new WebSocket(url);

      // A socket stuck in CONNECTING (exhausted browser connection pool) never
      // fires onclose, so nothing would ever retry it. Abandon and reschedule.
      this._wsConnectTimeout = setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
          // Don't .close() a CONNECTING socket — it doesn't release the handle.
          this.ws.onopen = null;
          this.ws.onclose = null;
          this.ws.onerror = null;
          this.ws.onmessage = null;
          this.ws = null;
          this.scheduleWsReconnect();
        }
      }, CONNECT_TIMEOUT_MS);

      this.ws.onopen = () => {
        clearTimeout(this._wsConnectTimeout);
        // The token travels in the first frame, never the URL: a query string
        // would be logged by every proxy on the way.
        this.ws.send(
          JSON.stringify({ type: 'auth', token: this.access_token }),
        );
      };

      this.ws.onmessage = (event) => {
        let msg;
        try {
          msg = JSON.parse(event.data);
        } catch {
          return;
        }
        if (msg.type === 'auth:ok') {
          this.wsAuthenticated = true;
          this.wsRetryCount = 0;
          this.onWhatsAppSocketOpen && this.onWhatsAppSocketOpen();
          return;
        }
        if (msg.type === 'auth:error') {
          this.wsAuthenticated = false;
          return;
        }
        this.onWhatsAppEvent && this.onWhatsAppEvent(msg);
      };

      // Only a close we did NOT initiate reaches this handler: disconnect()
      // detaches the handlers before closing, so there is no code to inspect and
      // no race where a socket we replaced schedules a reconnect for its
      // successor. A 4003 (auth failed) reconnects too — the token may simply
      // have expired and be about to refresh.
      this.ws.onclose = () => {
        clearTimeout(this._wsConnectTimeout);
        this.wsAuthenticated = false;
        this.onWhatsAppSocketClose && this.onWhatsAppSocketClose();
        this.scheduleWsReconnect();
      };

      this.ws.onerror = () => {
        /* onclose follows; nothing to do here */
      };
    },

    scheduleWsReconnect() {
      clearTimeout(this.wsReconnectTimer);
      // Never give up, just slow down. visibilitychange resets the counter.
      const slow = this.wsRetryCount >= SLOW_POLL_AFTER_ATTEMPTS;
      const delay = slow
        ? SLOW_POLL_MS
        : Math.min(1000 * Math.pow(2, this.wsRetryCount), MAX_BACKOFF_MS);
      this.wsRetryCount += 1;
      this.wsReconnectTimer = setTimeout(() => this.connectWebSocket(), delay);
    },

    ensureWebSocketConnected() {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) return;
      this.wsRetryCount = 0;
      this.connectWebSocket();
    },

    disconnectWebSocket() {
      clearTimeout(this.wsReconnectTimer);
      clearTimeout(this._wsConnectTimeout);
      if (!this.ws) return;

      const ws = this.ws;
      this.ws = null;
      this.wsAuthenticated = false;
      // Detach first: an onclose firing after we've replaced the socket would
      // schedule a reconnect against the live one.
      ws.onopen = null;
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      // Closing a CONNECTING socket does not release the browser's TCP handle;
      // dropping the reference is all we can do there.
      if (ws.readyState === WebSocket.OPEN) {
        ws.close(1000, 'closed');
      }
    },
  },
};
